<template>
  <div class="list-manager pa-4">
    <div class="d-flex align-center mb-6">
      <v-btn
        icon="mdi-arrow-left"
        variant="text"
        class="mr-2 rounded-lg"
        @click="goHome"
        title="Back to Home"
      ></v-btn>
      <v-btn
        icon
        variant="text"
        class="mr-2 rounded-lg"
        @click="isSidebarCollapsed = !isSidebarCollapsed"
        v-if="$vuetify.display.mdAndUp"
        :title="isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
      >
        <v-icon :icon="isSidebarCollapsed ? 'mdi-menu' : 'mdi-backburger'"></v-icon>
      </v-btn>
      <div class="d-flex align-center flex-grow-1 cursor-pointer no-select" @click="isMobileExpanded = !isMobileExpanded">
        <v-icon icon="mdi-playlist-play" color="primary" size="large" class="mr-3"></v-icon>
        <h2 class="text-h4 font-weight-bold">My Lists</h2>
        <v-icon 
          :icon="isMobileExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" 
          class="ml-2 d-md-none"
        ></v-icon>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        prepend-icon="mdi-plus"
        color="primary"
        variant="flat"
        class="rounded-lg px-4"
        @click.stop="showCreateDialog = true"
      >
        <span v-if="!isSidebarCollapsed || !$vuetify.display.mdAndUp">New List</span>
      </v-btn>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-grey">Loading your lists...</div>
    </div>

    <div v-else-if="lists.length === 0" class="text-center py-10">
      <v-icon icon="mdi-playlist-remove" size="64" color="grey-darken-2" class="mb-4"></v-icon>
      <h3 class="text-h5 text-grey">You don't have any lists yet</h3>
      <p class="text-body-2 text-grey-darken-1">Create lists to organize developers by engine, role, or interest.</p>
    </div>

    <div v-else class="list-layout-container">
      <v-expand-transition>
        <div 
          v-show="!$vuetify.display.xs || isMobileExpanded"
          :class="['sidebar-container', { 'collapsed': isSidebarCollapsed && !isHovered && $vuetify.display.mdAndUp }]"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <v-list class="floating-list rounded-xl border border-white-10 bg-black-20 h-100" density="compact">
            <v-list-item
              v-for="list in lists"
              :key="list.uri"
              :active="selectedListUri === list.uri"
              color="primary"
              class="mb-1 rounded-lg px-4"
              @click="handleListSelect(list.uri)"
            >
              <template v-slot:prepend>
                <v-avatar
                  color="primary"
                  variant="tonal"
                  size="32"
                  class="mr-2 border border-white-05"
                >
                  <v-img v-if="list.avatar" :src="list.avatar" cover>
                    <template v-slot:placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
                      </div>
                    </template>
                    <template v-slot:error>
                      <v-icon icon="mdi-playlist-play" size="small"></v-icon>
                    </template>
                  </v-img>
                  <v-icon v-else icon="mdi-playlist-play" size="small"></v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-bold list-name text-truncate">
                {{ list.name }}
              </v-list-item-title>
              <v-tooltip 
                activator="parent" 
                location="right" 
                offset="15"
                v-if="isSidebarCollapsed && !isHovered"
              >
                {{ list.name }}
              </v-tooltip>
            </v-list-item>
          </v-list>
        </div>
      </v-expand-transition>

      <div class="content-container flex-grow-1 min-width-0">
        <div v-if="!selectedList" class="text-center py-10 border border-white-05 rounded-xl bg-black-05">
           <p class="text-grey">Select a list to see its members</p>
        </div>
        <div v-else>
          <div class="d-flex align-center mb-6 px-2">
            <v-avatar
              size="64"
              color="primary"
              variant="tonal"
              class="mr-4 rounded-xl border border-white-05 shadow-lg"
            >
              <v-img v-if="selectedList.avatar" :src="selectedList.avatar" cover>
                <template v-slot:error>
                  <v-icon icon="mdi-playlist-play" size="large"></v-icon>
                </template>
              </v-img>
              <v-icon v-else icon="mdi-playlist-play" size="large"></v-icon>
            </v-avatar>
            <div class="flex-grow-1 min-width-0">
              <h3 class="text-h5 font-weight-bold text-white text-truncate">{{ selectedList.name }}</h3>
              <div 
                v-if="selectedList.description" 
                class="text-subtitle-2 text-grey-lighten-1" 
                style="max-width: 600px;"
                v-html="renderRichText(selectedList.description)"
                @click="handleContentClick"
              ></div>
            </div>
            
            <v-spacer></v-spacer>

            <v-btn icon variant="text" size="small">
              <v-icon icon="mdi-dots-vertical"></v-icon>
              <v-menu activator="parent" transition="slide-y-transition" location="bottom end">
                <v-list density="compact" class="rounded-lg shadow-lg">
                  <v-list-item
                    prepend-icon="mdi-pencil"
                    title="Edit List"
                    @click="openEditDialog"
                  ></v-list-item>
                  <v-list-item
                    prepend-icon="mdi-delete"
                    title="Delete List"
                    base-color="error"
                    @click="promptDeleteList(selectedList)"
                  ></v-list-item>
                </v-list>
              </v-menu>
            </v-btn>
          </div>

          <!-- Tabs -->
          <v-tabs v-model="activeTab" color="primary" class="mb-4 custom-tabs" density="comfortable">
            <v-tab value="members" class="text-none">
              <v-icon icon="mdi-account-group" class="mr-2"></v-icon>
              Members ({{ members.length }})
            </v-tab>
            <v-tab value="posts" class="text-none">
              <v-icon icon="mdi-post" class="mr-2"></v-icon>
              Posts
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab" class="rounded-xl overflow-hidden shadow-lg">
            <v-window-item value="members">
              <div v-if="fetchingMembers" class="text-center py-10">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <div v-else-if="members.length === 0" class="text-center py-10 text-grey border border-white-05 rounded-xl dashed">
                This list has no members yet.
              </div>
              <div v-else class="member-grid">
                <v-card
                  v-for="member in members"
                  :key="member.subject.did"
                  class="rounded-xl border border-white-10 bg-black-20 pa-4 mb-2 d-flex align-center cursor-pointer member-item"
                  @click="$emit('view-profile', member.subject.handle)"
                >
                  <v-avatar size="52" class="mr-4 border border-white-05">
                    <v-img v-if="member.subject.avatar" :src="member.subject.avatar"></v-img>
                    <v-icon v-else icon="mdi-account"></v-icon>
                  </v-avatar>
                  <div class="flex-grow-1 min-width-0">
                    <div class="text-subtitle-1 font-weight-black text-white text-truncate">
                      {{ member.subject.displayName || member.subject.handle }}
                    </div>
                    <div class="text-caption text-grey text-truncate">@{{ member.subject.handle }}</div>
                  </div>
                  <v-btn
                    icon="mdi-account-remove-outline"
                    variant="text"
                    color="error"
                    size="small"
                    @click.stop="handleRemoveMember(member.uri)"
                    title="Remove from list"
                  ></v-btn>
                </v-card>
              </div>
            </v-window-item>

            <v-window-item value="posts">
              <div class="feed-container">
                <div v-if="fetchingFeed" class="text-center py-10">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-2 text-caption text-grey">Fetching member posts...</div>
                </div>
                <div v-else-if="feed.length === 0" class="text-center py-10 text-grey border border-white-05 rounded-xl dashed">
                  No posts found from members in this list.
                </div>
                <div v-else class="list-feed">
                  <post-item
                    v-for="item in feed"
                    :key="item.post.uri"
                    :post="item.post"
                    @view-profile="$emit('view-profile', $event)"
                    @view-thread="$emit('view-thread', $event)"
                    @delete="handleDelete"
                    class="mb-4"
                  />
                  
                  <!-- Load More Button -->
                  <div v-if="cursor != null && cursor !== '' && !fetchingFeed" class="d-flex justify-center py-6">
                    <v-btn
                      color="primary"
                      variant="tonal"
                      rounded="pill"
                      size="large"
                      class="px-10 font-weight-black"
                      :loading="loadingMoreFeed"
                      @click="loadMoreFeed"
                    >
                      <v-icon start icon="mdi-chevron-down"></v-icon>
                      Load More
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </div>
      </div>
    </div>

    <!-- Create Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="400">
      <v-card class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur">
        <v-card-title class="pa-4 font-weight-bold">Create New List</v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-text-field
            v-model="newList.name"
            label="Name"
            placeholder="e.g. Engine Developers"
            variant="outlined"
            density="compact"
            class="rounded-lg mb-3"
            hide-details
            autofocus
          ></v-text-field>
          <v-textarea
            v-model="newList.description"
            label="Description (Optional)"
            variant="outlined"
            density="compact"
            rows="2"
            hide-details
            class="rounded-lg"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showCreateDialog = false; resetNewList()">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            :disabled="!newList.name.trim()" 
            :loading="creating"
            @click="handleCreateList" 
            class="rounded-lg px-6"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500">
      <v-card class="rounded-xl border border-white-10 bg-surface-variant backdrop-blur overflow-hidden text-white">
        <v-card-title class="pa-5 font-weight-bold d-flex align-center">
          <v-icon icon="mdi-pencil" class="mr-3" color="primary"></v-icon>
          Edit List
        </v-card-title>
        <v-card-text class="pa-5 pt-0">
          <div class="d-flex flex-column align-center mb-6">
            <v-hover v-slot="{ isHovering, props }">
              <v-avatar
                v-bind="props"
                size="120"
                class="mb-4 border-2 border-primary cursor-pointer position-relative shadow-lg rounded-xl overflow-hidden"
                @click="triggerFileInput"
              >
                <v-img v-if="editList.avatarPreview || editList.avatar" :src="editList.avatarPreview || editList.avatar" cover></v-img>
                <v-icon v-else icon="mdi-playlist-play" size="48"></v-icon>
                <div 
                  v-if="isHovering" 
                  class="position-absolute d-flex flex-column align-center justify-center w-100 h-100" 
                  style="background: rgba(0,0,0,0.5); z-index: 2"
                >
                  <v-icon icon="mdi-camera" color="white" size="32"></v-icon>
                  <span class="text-caption font-weight-bold">Change Icon</span>
                </div>
              </v-avatar>
            </v-hover>

            <v-text-field
              v-model="editList.name"
              label="List Name"
              variant="outlined"
              class="rounded-lg mb-4 w-100 custom-input-field"
              hide-details
            ></v-text-field>
            
            <v-textarea
              v-model="editList.description"
              label="Description"
              variant="outlined"
              rows="3"
              class="rounded-lg w-100 custom-input-field"
              hide-details
            ></v-textarea>
          </div>
          <input type="file" ref="fileInput" accept="image/*" class="d-none" @change="handleFileChange">
        </v-card-text>
        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" color="grey-lighten-1" @click="showEditDialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            :disabled="!editList.name.trim()" 
            :loading="updating"
            @click="handleUpdateList" 
            class="rounded-lg px-8 py-2 h-auto"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';
import { 
  getUserLists, 
  getListMembers, 
  getListFeed,
  createNativeList, 
  updateNativeList,
  removeActorFromList,
  uploadBlob,
  agent,
  currentUser,
  lastDeletedPostUri
} from '@/services/bluesky';
import PostItem from '@/components/PostItem.vue';

const router = useRouter();
const emit = defineEmits(['view-profile', 'view-thread', 'delete']);

const loading = ref(true);
const fetchingMembers = ref(false);
const fetchingFeed = ref(false);
const creating = ref(false);
const deleting = ref(false);
const updating = ref(false);
const lists = ref<any[]>([]);
const selectedListUri = ref<string | null>(null);
const members = ref<any[]>([]);
const feed = ref<any[]>([]);
const activeTab = ref('members');
const cursor = ref<string | undefined>(undefined);
const loadingMoreFeed = ref(false);
const isSidebarCollapsed = ref(true);
const isHovered = ref(false);
const isMobileExpanded = ref(false);

const handleListSelect = (uri: string) => {
  selectedListUri.value = uri;
  if (window.innerWidth < 960) {
    isMobileExpanded.value = false;
  }
};

const showCreateDialog = ref(false);
const newList = ref({ name: '', description: '' });

const showEditDialog = ref(false);
const editList = ref({ name: '', description: '', avatar: null as any, avatarPreview: null as string | null, pendingFile: null as File | null });
const fileInput = ref<HTMLInputElement | null>(null);

const showDeleteDialog = ref(false);
const listToDelete = ref<any>(null);

const selectedList = computed(() => 
  lists.value.find(l => l.uri === selectedListUri.value)
);

const fetchLists = async () => {
  loading.value = true;
  try {
    const { lists: userLists } = await getUserLists();
    lists.value = userLists;
    if (userLists.length > 0 && !selectedListUri.value) {
      selectedListUri.value = userLists[0].uri;
    }
  } catch (error) {
    console.error('Failed to fetch lists:', error);
  } finally {
    loading.value = false;
  }
};

const fetchMembers = async () => {
  if (!selectedListUri.value) {
    members.value = [];
    return;
  }
  
  fetchingMembers.value = true;
  try {
    const { items } = await getListMembers(selectedListUri.value);
    members.value = items;
  } catch (error) {
    console.error('Failed to fetch list members:', error);
  } finally {
    fetchingMembers.value = false;
  }
};

const fetchFeed = async () => {
  if (!selectedListUri.value) {
    feed.value = [];
    return;
  }
  
  fetchingFeed.value = true;
  cursor.value = undefined;
  try {
    const { feed: listFeed, cursor: nextCursor } = await getListFeed(selectedListUri.value);
    // Filter out replies from list feed
    feed.value = listFeed.filter((item: any) => !item.reply);
    cursor.value = nextCursor;
  } catch (error) {
    console.error('Failed to fetch list feed:', error);
  } finally {
    fetchingFeed.value = false;
  }
};

const loadMoreFeed = async () => {
  if (loadingMoreFeed.value || !cursor.value || !selectedListUri.value) return;
  loadingMoreFeed.value = true;
  try {
    const { feed: newItems, cursor: nextCursor } = await getListFeed(selectedListUri.value, 50, cursor.value);
    const filteredItems = newItems.filter((item: any) => !item.reply);
    feed.value = [...feed.value, ...filteredItems];
    cursor.value = nextCursor;
  } catch (error) {
    console.error('Failed to load more list feed:', error);
  } finally {
    loadingMoreFeed.value = false;
  }
};

const goHome = () => router.push('/');

const handleContentClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router
  });
};

const handleDelete = (post: any) => {
  emit('delete', post);
};

watch(lastDeletedPostUri, (newUri) => {
  if (newUri) {
    feed.value = feed.value.filter(item => (item.post?.uri || item.uri) !== newUri);
  }
});

const handleCreateList = async () => {
  if (!newList.value.name.trim()) return;
  creating.value = true;
  try {
    await createNativeList(newList.value.name, newList.value.description);
    showCreateDialog.value = false;
    resetNewList();
    await fetchLists();
  } catch (error) {
    console.error('Failed to create list:', error);
  } finally {
    creating.value = false;
  }
};

const promptDeleteList = (list: any) => {
  listToDelete.value = list;
  showDeleteDialog.value = true;
};

const openEditDialog = () => {
  if (!selectedList.value) return;
  editList.value = {
    name: selectedList.value.name,
    description: selectedList.value.description || '',
    avatar: selectedList.value.avatar,
    avatarPreview: null,
    pendingFile: null
  };
  showEditDialog.value = true;
};

const triggerFileInput = () => fileInput.value?.click();

const handleFileChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) {
    editList.value.pendingFile = file;
    editList.value.avatarPreview = URL.createObjectURL(file);
  }
};

const handleUpdateList = async () => {
  if (!selectedList.value || !editList.value.name.trim()) return;
  updating.value = true;
  try {
    let avatarBlob = undefined;
    if (editList.value.pendingFile) {
      avatarBlob = await uploadBlob(editList.value.pendingFile, editList.value.pendingFile.type);
    }
    
    await updateNativeList(selectedList.value.uri, {
      name: editList.value.name,
      description: editList.value.description,
      avatar: avatarBlob
    });
    
    showEditDialog.value = false;
    await fetchLists();
  } catch (error) {
    console.error('Failed to update list:', error);
  } finally {
    updating.value = false;
  }
};

const confirmDeleteList = async () => {
  if (!listToDelete.value || !currentUser.value) return;
  deleting.value = true;
  try {
    const rkey = listToDelete.value.uri.split('/').pop();
    await agent.com.atproto.repo.deleteRecord({
      repo: currentUser.value.did,
      collection: 'app.bsky.graph.list',
      rkey,
    });
    
    showDeleteDialog.value = false;
    
    // Reset selection if deleted
    if (selectedListUri.value === listToDelete.value?.uri) {
      selectedListUri.value = null;
    }
    
    listToDelete.value = null;
    await fetchLists();
  } catch (error) {
    console.error('Failed to delete list:', error);
  } finally {
    deleting.value = false;
  }
};

const handleRemoveMember = async (itemUri: string) => {
  try {
    await removeActorFromList(itemUri);
    await fetchMembers(); // Refresh
  } catch (error) {
    console.error('Failed to remove member:', error);
  }
};

const resetNewList = () => {
  newList.value = { name: '', description: '' };
};

watch(selectedListUri, () => {
  // Reset data arrays to force fresh fetch and show loading states
  members.value = [];
  feed.value = [];
  
  if (activeTab.value === 'members') fetchMembers();
  else fetchFeed();
});

watch(activeTab, (val) => {
  if (val === 'members' && members.value.length === 0) fetchMembers();
  else if (val === 'posts' && feed.value.length === 0) fetchFeed();
});

onMounted(() => {
  fetchLists();
});
</script>

<style scoped>
.list-manager {
  max-width: 1400px;
  margin: 0 auto;
}
.bg-black-05 {
  background: rgba(0, 0, 0, 0.2);
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.dashed {
  border-style: dashed !important;
}
.backdrop-blur {
  backdrop-filter: blur(20px);
}

.list-layout-container {
  position: relative;
  display: flex;
  min-height: 600px;
}

.sidebar-container {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
  z-index: 100;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  overflow: hidden;
}

.sidebar-container .floating-list {
  pointer-events: auto;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(40px) saturate(180%);
  overflow-x: hidden;
}

.sidebar-container.collapsed {
  width: 64px;
}

.sidebar-container.collapsed :deep(.v-list-item) {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.sidebar-container.collapsed .list-name {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
}

.content-container {
  width: 100%;
  padding-left: 84px;
  transition: padding-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.bg-black-20 {
  background: rgba(0, 0, 0, 0.4) !important;
}

.border-white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.member-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.member-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.member-item:hover {
  border-color: var(--v-theme-primary) !important;
  background: rgba(var(--v-theme-primary), 0.05) !important;
  transform: translateX(4px);
}

.custom-tabs :deep(.v-slide-group__content) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.custom-input-field :deep(.v-field) {
  background: rgba(255, 255, 255, 0.03) !important;
  border-radius: 12px;
}

.hover-opacity-100 {
  opacity: 0.8 !important;
}

.transition-opacity {
  transition: opacity 0.2s;
}

@media (max-width: 960px) {
  .sidebar-container {
    position: relative;
    width: 100% !important;
    margin-bottom: 24px;
  }
  .content-container {
    padding-left: 0 !important;
  }
}

.min-width-0 {
  min-width: 0;
}
</style>
